import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { useCart } from '@/providers/CartProvider'
import { useBranding } from '@/branding/BrandingProvider'
import type { Order } from '@/types'

export function OrderHistoryPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const { siteName } = useBranding()
  const [searchParams] = useSearchParams()
  const { clearCart } = useCart()
  const checkoutStatus = searchParams.get('checkout')

  useEffect(() => {
    api
      .get<Order[]>('/checkout/orders')
      .then(setOrders)
      .catch((err) => console.error('Failed to load orders:', err))
      .finally(() => setLoading(false))
  }, [])

  // Clear local cart state when Stripe redirected back with checkout=success.
  // The backend cart was already cleared by the webhook; this just syncs the
  // local state if the user never navigates away.
  useEffect(() => {
    if (checkoutStatus === 'success') {
      clearCart()
    }
  }, [checkoutStatus, clearCart])

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-100 h-16 flex items-center px-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-navy flex items-center justify-center">
            <Icon icon="solar:health-linear" className="text-white text-sm" />
          </div>
          <span className="text-lg font-medium text-navy tracking-tight">{siteName}</span>
        </Link>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/account" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy mb-6 transition-colors">
          <Icon icon="solar:arrow-left-linear" /> Back to Dashboard
        </Link>

        <h1 className="text-2xl font-medium tracking-tight text-navy mb-8">Order History</h1>

        {checkoutStatus === 'success' && (
          <div className="mb-6 p-4 rounded-xl border border-emerald-200 bg-emerald-50 flex items-start gap-3">
            <Icon icon="solar:check-circle-bold" className="text-emerald-600 text-xl mt-0.5" />
            <div>
              <p className="font-medium text-emerald-800">Payment received</p>
              <p className="text-sm text-emerald-700">
                Thank you for your order. A confirmation has been sent to your email.
              </p>
            </div>
          </div>
        )}
        {checkoutStatus === 'cancelled' && (
          <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-3">
            <Icon icon="solar:info-circle-bold" className="text-amber-600 text-xl mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Checkout cancelled</p>
              <p className="text-sm text-amber-700">
                Your cart was saved. You can complete checkout any time.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy mx-auto" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <Icon icon="solar:bag-4-linear" className="text-5xl text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-light">No orders yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order._id} className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-slate-400 font-light">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </span>
                  <span className="text-xs font-medium px-3 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">
                    {order.paymentStatus === 'paid' ? 'paid' : order.status}
                  </span>
                </div>
                <div className="text-sm text-slate-600 mb-2">
                  {(order.items || []).length} item{(order.items || []).length !== 1 ? 's' : ''}
                </div>
                <div className="text-lg font-medium text-navy">${order.total}</div>
                {order.shippingAddress?.line1 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600">
                    <div className="font-medium text-slate-700 mb-1">Shipping to</div>
                    <div>
                      {order.shippingAddress.name && <>{order.shippingAddress.name}<br /></>}
                      {order.shippingAddress.line1}
                      {order.shippingAddress.line2 && <>, {order.shippingAddress.line2}</>}
                      <br />
                      {order.shippingAddress.city}
                      {order.shippingAddress.state && `, ${order.shippingAddress.state}`}{' '}
                      {order.shippingAddress.postalCode}
                      <br />
                      {order.shippingAddress.country}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
